import { DOI_RESOLVER_URL_PREFIXES } from '@/constants/urls';

/**
 * Shared DOI utilities.
 */

/**
 * Permissive DOI regex for user input validation.
 * - Allows additional sub-prefix segments (e.g., 10.xxxx.y/ ...)
 * - Accepts any non-whitespace suffix (permits percent-encoding)
 */
export const DOI_INPUT_REGEX = /^10\.\d{4,}(?:\.\d+)*\/\S+$/i;

/**
 * Stricter DOI regex useful for parsing/extraction from text.
 * - Commonly used character class for Crossref DOIs
 */
export const DOI_STRICT_REGEX = /^10\.\d{4,9}\/[\-._;()/:A-Z0-9]+$/i;

/** Canonical key form for comparing/deduping DOIs: trimmed, lower-cased. */
export const normalizeDoi = (doi: string): string => doi.trim().toLowerCase();

/**
 * Removes common resolver prefixes from a DOI input string.
 * Handles schemeful and schemeless resolver variants.
 */
export function stripResolverPrefixes(input: string): string {
  if (typeof input !== 'string') return input as unknown as string;
  let cleaned = input.trim();
  const patterns = [
    /^https?:\/\/doi\.org\//i,
    /^https?:\/\/dx\.doi\.org\//i,
    /^doi\.org\//i,
    /^dx\.doi\.org\//i,
  ];
  for (const p of patterns) cleaned = cleaned.replace(p, '');
  return cleaned;
}

/**
 * Sanitizes a raw DOI input by removing common resolver prefixes and any
 * whitespace left around the result.
 *
 * The single definition of "sanitize a DOI input": both the non-throwing
 * live-typing path (DoiSearchBox via useDoiSearch) and the throwing
 * search/edit path (`sanitizeAndValidateDoi` in workImport) derive from this,
 * so there is no second copy to keep in step.
 */
export const sanitizeDoi = (input: string): string =>
  stripResolverPrefixes(input).trim();

/** Returns true when a sanitized DOI matches the permissive input format. */
export const validateDoi = (doi: string): boolean => DOI_INPUT_REGEX.test(doi);

/**
 * Returns true when the value is an HTTPS DOI resolver URL (doi.org / dx.doi.org).
 */
export function isDoiResolverUrl(value: string): boolean {
  if (typeof value !== 'string') {
    return false;
  }
  const normalized = value.trim().toLowerCase();
  return DOI_RESOLVER_URL_PREFIXES.some((prefix) =>
    normalized.startsWith(prefix)
  );
}
